import { useEffect } from 'react';

/**
 * Fixes reCAPTCHA v2 image challenge inside a Radix modal dialog.
 *
 * When Radix Dialog opens with modal={true} (default), it:
 * 1. Sets `pointer-events: none` on `<body>` — blocks ALL clicks
 * 2. Adds `aria-hidden="true"` on body-level siblings — makes elements inert
 *
 * Google reCAPTCHA v2 renders its image challenge as direct children of `<body>`
 * with `z-index: 2000000000`. Radix blocks them with both mechanisms.
 *
 * This hook:
 * - Resets `pointer-events` on `<body>` so the challenge is clickable
 * - Watches for reCAPTCHA elements and removes `aria-hidden` + restores `pointer-events`
 *
 * Confirmed via browser inspection:
 * - body.style.pointerEvents = "none" (set by Radix)
 * - reCAPTCHA divs: aria-hidden="true", pointerEvents="none" (inherited from body)
 *
 * Usage: Call in any dialog that renders reCAPTCHA:
 *   useRecaptchaDialogFix(open);
 */
/**
 * A body-level element this hook should unblock. reCAPTCHA is recognised by its huge z-index, but
 * dev overlays use the same trick: react-grab (local dev, `VITE_REACT_GRAB`) mounts a full-screen,
 * click-through layer at z-index 2147483647. Forcing `pointer-events: auto` on THAT turned it into
 * an invisible wall over the whole page, so nothing in the dialog could be clicked or typed into.
 */
function isRecaptchaLayer(el: HTMLElement): boolean {
  if (el.hasAttribute('data-react-grab')) return false;
  return parseInt(window.getComputedStyle(el).zIndex, 10) > 1000000;
}

export function useRecaptchaDialogFix(dialogOpen: boolean) {
  useEffect(() => {
    if (!dialogOpen) return;

    // Reset pointer-events on body — Radix sets body { pointer-events: none }
    const resetBodyPointerEvents = () => {
      document.body.style.pointerEvents = '';
    };
    const timer = setTimeout(resetBodyPointerEvents, 0);

    // Watch for reCAPTCHA challenge elements and fix them
    const observer = new MutationObserver((mutations) => {
      // Always ensure body pointer-events is reset (Radix may re-apply it)
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }

      for (const mutation of mutations) {
        // New elements added to body (reCAPTCHA challenge container)
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              if (isRecaptchaLayer(node)) {
                node.removeAttribute('aria-hidden');
                node.style.pointerEvents = 'auto';
              }
            }
          });
        }
        // Radix re-applying aria-hidden to existing reCAPTCHA elements
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'aria-hidden' &&
          mutation.target instanceof HTMLElement
        ) {
          const el = mutation.target;
          if (isRecaptchaLayer(el) && el.getAttribute('aria-hidden') === 'true') {
            el.removeAttribute('aria-hidden');
            el.style.pointerEvents = 'auto';
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'style'],
      subtree: false,
    });

    // Also fix any already-existing reCAPTCHA elements
    document.querySelectorAll<HTMLElement>('body > div').forEach((el) => {
      if (isRecaptchaLayer(el)) {
        el.removeAttribute('aria-hidden');
        el.style.pointerEvents = 'auto';
      }
    });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [dialogOpen]);
}
